import { useState, ChangeEvent } from 'react';
import { Divider, Card, InlineTextField } from '@care/react-component-lib';
import { Box, Button, Typography, Theme } from '@mui/material';
import getConfig from 'next/config';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { Icon24InfoLocation } from '@care/react-icons';
import useMediaQuery from '@mui/material/useMediaQuery';

import { costGuide_costGuide_CostGuideSuccess_costGuide_rates_averageRate as AverageRatesType } from '@/__generated__/costGuide';
import { UsStateCode } from '@/__generated__/globalTypes';
import formatNumToString from '@/utils/formatNumToString';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

interface HeroProps {
  primaryHeadline: string | null;
  serviceNameLowercase: string;
  enrollmentUrl: string;
  city?: string | null;
  state: UsStateCode | null;
  rateLastUpdatedDate: string | null;
  averageRates: AverageRatesType | null;
  serviceNamePluralLowercase: string;
}

const Hero = ({
  primaryHeadline,
  serviceNameLowercase,
  enrollmentUrl,
  city,
  state,
  rateLastUpdatedDate,
  averageRates,
  serviceNamePluralLowercase,
}: HeroProps) => {
  const avgHourlyRate = averageRates?.averageHourlyRate?.rate?.amount
    ? parseFloat(averageRates.averageHourlyRate.rate.amount).toFixed(2)
    : null;
  const avgWeeklyRate = averageRates?.averageWeeklyRate?.rate?.amount
    ? formatNumToString(Math.round(parseFloat(averageRates.averageWeeklyRate.rate.amount)))
    : null;
  const avgMonthlyRate = averageRates?.averageMonthlyRate?.rate?.amount
    ? formatNumToString(Math.round(parseFloat(averageRates.averageMonthlyRate.rate.amount)))
    : null;
  const zipCodeRegex = /^\d{0,5}$/;
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [zip, setZip] = useState({
    zipCode: '',
  });

  const onZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const zipCodeVal = e.target.value;

    if (!zipCodeRegex.test(zipCodeVal)) {
      return;
    }
    setZip((prevValues) => ({
      ...prevValues,
      zipCode: zipCodeVal,
    }));
  };

  const enrollmentLink = zip.zipCode
    ? `${enrollmentUrl}&zipcode=${zip.zipCode}`
    : `${enrollmentUrl}`;

  const ctaText = 'Join free to get started';

  const handleCtaBtn = () => {
    const amplitudeData = {
      cta_text: ctaText,
      cta_type: 'button',
      url: `${NEXT_PUBLIC_APP_URL}${enrollmentLink}`,
      url_path: enrollmentLink,
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCtaBtn();
      globalThis.location.href = enrollmentLink;
    }
  };

  const useServiceName = () => {
    if (
      serviceNameLowercase.includes('child care') ||
      serviceNameLowercase.includes('senior care') ||
      serviceNameLowercase.includes('pet care') ||
      serviceNameLowercase.includes('housekeeping') ||
      serviceNameLowercase.includes('house cleaning')
    ) {
      return serviceNamePluralLowercase;
    }

    return `a ${serviceNameLowercase}`;
  };

  const serviceName = useServiceName();
  const subheadlineIntro = rateLastUpdatedDate
    ? `As of ${rateLastUpdatedDate}`
    : `According to our latest data`;

  return (
    <Box maxWidth="1062px" px={{ xs: 3, sm: 5, md: 20, lg: 0 }} py={{ xs: 2, sm: 5, md: 7 }}>
      <Card careVariant="subtle">
        <Box px={{ xs: 4, sm: 6, md: 10, lg: 13 }} pt={{ xs: 4, md: 7 }} pb={4}>
          {primaryHeadline && (
            <Typography
              variant="h1"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(he.decode(primaryHeadline)),
              }}
              fontSize={{ md: 'display3.fontSize' }}
              pb={{ md: 2 }}
            />
          )}
          <Box mt={3} mb={2}>
            <Typography
              variant="h2"
              color="care.blue.500"
              fontWeight="700"
              fontSize={{ xs: 'display3.fontSize', md: 'display1.fontSize' }}>
              <span>{`$${avgHourlyRate}`}</span>
              <Typography
                component="span"
                fontSize={{ xs: 'h2.fontSize', md: 'display3.fontSize' }}
                fontWeight="700">
                /hour
              </Typography>
            </Typography>
          </Box>
          <Typography variant={isMobileOrSmaller ? 'body3' : 'body1'}>
            <span>{`${subheadlineIntro}, the average starting cost of ${serviceName} in ${city}, ${state} is${' '}`}</span>
            <Typography
              component="span"
              fontSize="inherit"
              fontWeight="700">{`$${avgHourlyRate} per hour.${' '}`}</Typography>
            <span>{`The weekly rate for ${serviceName} in ${city}, ${state} is $${avgWeeklyRate}${' '}for working a 40-hour week.${' '}`}</span>
            <span>{`And the monthly cost is approximately $${avgMonthlyRate} for 130 hours of work.`}</span>
          </Typography>
          <Box width={{ xs: '90px', md: '180px' }}>
            <Divider />
          </Box>
          <Box pb={2}>
            <Typography
              variant={
                isMobileOrSmaller ? 'h4' : 'h2'
              }>{`Find ${serviceName} in your area`}</Typography>
          </Box>
          <Box pb={{ xs: 2, sm: 0 }} display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
            <InlineTextField
              autoFocus={!isMobileOrSmaller}
              id="zipCode"
              name="zipCode"
              placeholder="ZIP code"
              value={zip.zipCode}
              onKeyUp={handleKeyUp}
              onChange={onZipCodeChange}
              InputProps={{
                endAdornment: <Icon24InfoLocation />,
              }}
              sx={(theme: Theme) => ({
                paddingX: 0,
                maxWidth: '279px',
                paddingBottom: 2,

                // removes number arrows from Chrome, Safari, Edge, Opera
                '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                  appearance: 'none',
                  margin: 0,
                },
                // removes number arrows from Firefox
                '& input[type=text]': {
                  cursor: 'auto',
                  padding: theme.spacing(2, 2, 2, 0),
                  fontSize: theme.typography.body2.fontSize,
                },

                '.MuiInputBase-root': {
                  paddingTop: 2,
                },

                '.MuiInputBase-adornedEnd svg': {
                  marginRight: 0,
                  marginTop: '-5px !important',
                },

                [theme.breakpoints.up('sm')]: {
                  paddingRight: 2,
                },
              })}
            />
            <Box width={{ xs: '279px', sm: '321px' }} pt={{ sm: 0.5 }}>
              <Button
                color="primary"
                component="a"
                fullWidth
                onClick={handleCtaBtn}
                href={enrollmentLink}
                size="large"
                variant="contained"
                sx={{ fontWeight: '700' }}
                rel="nofollow">
                {ctaText}
              </Button>
            </Box>
          </Box>
          {rateLastUpdatedDate && (
            <Typography
              variant="info2"
              pt={0.5}
              color="care.grey.600"
              fontSize={{ md: 'info1.fontSize' }}>
              {`Data is based on rates in caregiver and provider profiles on Care.com. Last updated in ${rateLastUpdatedDate}.`}
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Hero;
